/* Motion 13.4.4, self-hosted. No React, remote runtime, or commerce dependency.
   mount(root)/destroy() can also follow Shopify section load/unload events. */
(function () {
  'use strict';
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  const narrow = matchMedia('(max-width: 720px)');
  const finePointer = matchMedia('(pointer: fine)');
  // Hussain's explicit 28 September instruction: keep mobile site motion active.
  // This overrides the OS reduced-motion request on small/touch-first screens.
  const shouldReduceMotion = () => preference.matches && !narrow.matches && finePointer.matches;
  const precise = matchMedia('(hover: hover) and (pointer: fine)');
  const ease = [.22, .8, .2, 1];
  let dispose = () => {};
  let mountedRoot;

  function mount(root) {
    dispose();
    mountedRoot = root;
    const cleanup = [];
    const animations = new Set();
    const touched = new Map();
    const scrollScenes = [];
    const frameScenes = [];
    let scrollFrame = 0;
    const controller = new AbortController();
    const signal = controller.signal;
    const mobile = narrow.matches;
    const rtl = document.documentElement.dir === 'rtl' ? -1 : 1;
    const reduced = shouldReduceMotion();
    const engine = window.Motion;
    document.body.classList.add('motion-ready');
    document.body.dataset.motion = reduced ? 'reduced' : engine ? 'active' : 'fallback';

    function remember(el) {
      if (!touched.has(el)) touched.set(el, el.getAttribute('style'));
      return el;
    }
    function play(el, values, options = {}) {
      remember(el);
      const animation = engine.animate(el, values, { duration: .75, ease, ...options });
      animations.add(animation);
      return animation;
    }
    function link(el, values, target, offsets = ['start end', 'end start']) {
      if (!el || !target) return;
      const animation = play(el, values, { duration: 1, ease: 'linear', autoplay: false });
      animation.pause();
      scrollScenes.push({ animation, target, offsets });
    }
    dispose = () => {
      controller.abort();
      cancelAnimationFrame(scrollFrame);
      animations.forEach(animation => animation.cancel());
      touched.forEach((style, el) => style === null ? el.removeAttribute('style') : el.setAttribute('style', style));
      cleanup.forEach(stop => stop());
      document.querySelector('.journey-progress')?.remove();
    };

    const reveals = [...root.querySelectorAll('[data-reveal]')];
    reveals.forEach(el => el.classList.add('is-visible'));
    if (reduced || !engine) return;

    // Content near the fold appears immediately; offscreen content never waits on a timer.
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        const rail = target.closest('[data-product-rail]');
        const card = target.matches('.product-card, .daily-card, .bestseller-card');
        const delay = rail ? Math.min([...rail.children].indexOf(target) % 4, 3) * .045 : 0;
        const map = target.matches('.origin-map');
        const distance = mobile ? 32 : card ? 90 : 80;
        if (map) { target.style.opacity = '1'; return; }
        play(target, {
          opacity: [0, 1],
          transform: map
            ? [`perspective(1400px) translateY(${distance}px) rotateX(${mobile ? 2 : 5}deg) scale(.98)`, 'perspective(1400px) translateY(0px) rotateX(0deg) scale(1)']
            : [`translateY(${distance}px)`, 'translateY(0px)']
        }, { duration: mobile ? .7 : 1.05, delay: card ? delay * 2 : delay }).then(() => {
          // Release transform ownership so the existing hover treatments still work.
          if (target.isConnected) { target.style.removeProperty('transform'); target.style.removeProperty('opacity'); }
        });
      });
    }, { threshold: .08, rootMargin: '0px 0px -20px 0px' });
    cleanup.push(() => observer.disconnect());
    reveals.forEach(el => {
      remember(el);
      el.classList.add('motion-reveal');
      // Purchase details must remain immediately available and stable.
      if (document.body.dataset.route.startsWith('/product/') || document.body.dataset.route === '/shop' || el.closest('.products .product-rail')) return;
      if (el.getBoundingClientRect().top > innerHeight + 28) el.style.opacity = '0';
      observer.observe(el);
    });

    const hero = root.querySelector('.hero');
    if (hero) {
      // One short, native sticky scene: the existing film changes from backdrop to
      // an editorial frame. No duplicate video, wheel interception or long scroll tunnel.
      const stage = document.createElement('div');
      stage.className = 'hero-stage';
      while (hero.firstChild) stage.append(hero.firstChild);
      hero.append(stage);
      const film = document.createElement('div');
      film.className = 'hero-film';
      stage.prepend(film);
      stage.querySelectorAll('.hero-poster, .hero-video, .hero-wash, .hero-grain').forEach(el => film.append(el));
      hero.classList.add('hero--choreographed');
      const content = hero.querySelector('.hero-content');
      const headline = hero.querySelector('h1');
      remember(headline);
      remember(content);
      cleanup.push(() => {
        film.replaceWith(...film.childNodes);
        stage.replaceWith(...stage.childNodes);
        hero.classList.remove('hero--choreographed');
      });
      frameScenes.push(() => {
        const rect = hero.getBoundingClientRect();
        const distance = Math.max(1, hero.offsetHeight - stage.offsetHeight);
        const raw = Math.max(0, Math.min(1, -rect.top / distance));
        const p = raw * raw * (3 - 2 * raw);
        film.style.width = `${100 - (mobile ? 8 : 48) * p}%`;
        film.style.height = `${100 - (mobile ? 55 : 38) * p}%`;
        film.style.top = `${(mobile ? 14 : 20) * p}%`;
        film.style.left = `${(mobile ? 4 : rtl === -1 ? 3 : 45) * p}%`;
        film.style.borderRadius = `${(mobile ? 18 : 28) * p}px`;
        film.style.boxShadow = `0 ${30*p}px ${100*p}px rgba(20,0,3,${.45*p}), 0 0 0 ${p}px rgba(255,255,255,.25)`;
        headline.style.scale = `${1 - (mobile ? .12 : .27) * p}`;
        content.style.translate = `0 ${mobile ? 28*p : -20*p}px`;
        hero.style.setProperty('--scene-progress', p);
      });
      const lines = [...hero.querySelectorAll('h1 > span')];
      lines.forEach((line, index) => {
        play(line, {
          opacity: [0, 1],
          clipPath: ['inset(0 0 100% 0)', 'inset(0 0 -12% 0)'],
          transform: [`translate3d(${(index ? 1 : -1) * rtl * (mobile ? 45 : 130)}px,${mobile ? 35 : 75}px,0) rotate(${index ? -3 : 3}deg)`, 'translate3d(0px,0px,0) rotate(0deg)']
        }, { delay: .12 + index * .22, duration: 1.3 });
      });
      hero.querySelectorAll('.eyebrow, .hero-lower, .film-index').forEach((el, index) => {
        play(el, { opacity: [0, 1], translate: ['0 16px', '0 0px'] }, { delay: .18 + index * .12, duration: .85 });
      });
      link(hero.querySelector('.film-index'), { opacity: [1, 0] }, hero, ['start -10%', 'center start']);
    }

    // The first products unfold from a loose overlapping fan into the real row.
    // Keep the labels and purchasing controls stationary; animate only the imagery.
    const firstRail = root.querySelector('.products .product-rail');
    if (firstRail) [...firstRail.children].forEach((card, index) => {
      const visual = card.querySelector('.product-visual');
      const slot = index % (mobile ? 2 : 5);
      const centre = mobile ? .5 : 2;
      link(visual, {
        transform: [`translate3d(${(centre-slot)*rtl*(mobile?36:72)}px,${mobile?65:120+Math.abs(centre-slot)*22}px,0) rotate(${(slot-centre)*(mobile?9:8)}deg) scale(.78)`, 'translate3d(0px,0px,0) rotate(0deg) scale(1)']
      }, firstRail, ['start 100%', 'start 43%']);
    });

    // Editorial motion travels inside existing geometry: no pinned-scroll chapters.
    root.querySelectorAll('.roast-story figure, .academy-world__media, .story-opening figure, .story-image, .wholesale-teaser__media').forEach(container => {
      const image = container.querySelector('img');
      if (!image) return;
      container.classList.add('motion-media');
      link(image, {
        transform: [`translateY(${mobile ? 18 : 65}px) scale(1.2)`, `translateY(${mobile ? -18 : -65}px) scale(1.2)`]
      }, container);
    });
    const filmWall = root.querySelector('.wholesale-hero__films');
    if (filmWall) link(filmWall, { rotate: [mobile ? '1deg' : '3deg', mobile ? '-1deg' : '-2deg'] }, filmWall.parentElement);

    // Whole headings preserve Arabic joining, line breaks and all approved copy.
    root.querySelectorAll('.products-heading h2, .daily-picks > header h2, .best-sellers > header h2, .academy-world__copy h2, .wholesale-teaser__copy h2, .story-timeline > header h2, .film-room > header h2').forEach((heading, index) => {
      heading.classList.add('motion-heading');
      const travel = (index % 2 ? -1 : 1) * rtl * (mobile ? 32 : 140);
      link(heading, { translate: [`${travel}px 0`, '0px 0'] }, heading.parentElement, ['start 95%', 'start 45%']);
    });

    // The Academy's two photographs converge into their final layout on scroll.
    // Existing section height and native scroll speed are unchanged.
    const academy = root.querySelector('.academy-world');
    if (academy) {
      link(academy.querySelector('.academy-world__main'), {
        transform: [`translate3d(${-rtl * (mobile ? 28 : 100)}px,${mobile ? 35 : 85}px,0) rotate(-8deg) scale(.88)`, 'translate3d(0,0,0) rotate(0deg) scale(1)']
      }, academy, ['start 95%', 'start 15%']);
      link(academy.querySelector('.academy-world__secondary'), {
        transform: [`translate3d(${rtl * (mobile ? 28 : 110)}px,${mobile ? 65 : 160}px,0) rotate(10deg) scale(.82)`, 'translate3d(0,0,0) rotate(0deg) scale(1)']
      }, academy, ['start 95%', 'start 5%']);
    }

    // A quiet depth response follows the pointer only on editorial imagery.
    if (precise.matches && !mobile) {
      root.querySelectorAll('.film-card, .gateway').forEach(card => {
        const image = card.querySelector('img');
        if (!image) return;
        remember(image);
        let frame = 0;
        let tilt;
        let last;
        const update = event => {
          last = event;
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = 0;
            const rect = card.getBoundingClientRect();
            const x = ((last.clientX - rect.left) / rect.width - .5) * 5;
            const y = ((last.clientY - rect.top) / rect.height - .5) * -5;
            tilt?.stop();
            tilt = play(image, { transform: `perspective(900px) rotateX(${y}deg) rotateY(${x}deg) scale(1.035)` }, { duration: .35 });
          });
        };
        card.addEventListener('pointermove', update, { passive: true, signal });
        card.addEventListener('pointerleave', () => {
          cancelAnimationFrame(frame); frame = 0;
          tilt?.stop();
          tilt = play(image, { transform: 'perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)' }, { duration: .55 });
        }, { signal });
        cleanup.push(() => cancelAnimationFrame(frame));
      });
    }
    const progress = document.createElement('div');
    progress.className = 'journey-progress';
    progress.setAttribute('aria-hidden', 'true');
    document.body.append(progress);
    const point = (value, size) => value === 'start' ? 0 : value === 'center' ? size / 2 : value === 'end' ? size : parseFloat(value) * size / 100;
    const renderScroll = () => {
      scrollFrame = 0;
      // Read every rectangle before writing. A single event-driven frame also works
      // in touch browsers whose native ViewTimeline attaches to the wrong scroller.
      const positions = scrollScenes.map(scene => {
        const rect = scene.target.getBoundingClientRect();
        const [ta, va] = scene.offsets[0].split(' ');
        const [tb, vb] = scene.offsets[1].split(' ');
        const start = point(va, innerHeight) - point(ta, rect.height);
        const travel = point(tb, rect.height) - point(ta, rect.height) - point(vb, innerHeight) + point(va, innerHeight);
        return Math.max(0, Math.min(1, (start - rect.top) / (travel || 1)));
      });
      const journey = scrollY / Math.max(1, document.documentElement.scrollHeight - innerHeight);
      scrollScenes.forEach((scene, index) => { scene.animation.time = positions[index]; });
      frameScenes.forEach(render => render());
      progress.style.transform = `scaleX(${Math.max(0, Math.min(1, journey))})`;
    };
    const scheduleScroll = () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(renderScroll); };
    window.addEventListener('scroll', scheduleScroll, { passive: true, signal });
    window.addEventListener('resize', scheduleScroll, { passive: true, signal });
    root.addEventListener('load', scheduleScroll, { capture: true, signal });
    document.fonts.ready.then(() => { if (!signal.aborted) scheduleScroll(); });
    scheduleScroll();
  }
  preference.addEventListener('change', () => mountedRoot?.isConnected && mount(mountedRoot));
  narrow.addEventListener('change', () => mountedRoot?.isConnected && mount(mountedRoot));
  finePointer.addEventListener('change', () => mountedRoot?.isConnected && mount(mountedRoot));
  window.MaridMotion = { mount, destroy: () => dispose(), shouldReduceMotion, version: '2026-09-28.4' };
}());
